import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import AgentSelect from "../components/common/AgentSelect";
import PageLoader from "../components/common/PageLoader";
import PersonCell from "../components/common/PersonCell";
import StatusBadge from "../components/common/StatusBadge";
import Table from "../components/common/Table";
import Tabs from "../components/common/Tabs";
import { agentApi } from "../lib/apiClient";
import { showApiError, showSuccessAction } from "../lib/alerts";
import { fetchCurrentAgent } from "../lib/agentAuth";
import { canAccessFinance, roleFromProfile } from "../lib/agencyAccess";

const REPORT_OPTIONS = [
  { value: "bookings", label: "Bookings" },
  { value: "passengers", label: "Passengers" },
  { value: "payments", label: "Payments" },
  { value: "outstanding", label: "Outstanding" },
  { value: "cancellations", label: "Cancellations" },
  { value: "documents", label: "Documents" },
  { value: "visa", label: "Visa" },
  { value: "travel", label: "Travel" },
  { value: "customers", label: "Customers" },
  { value: "notifications", label: "Notifications" },
];

const STATUS_OPTIONS = {
  bookings: [
    { value: "draft", label: "Draft" },
    { value: "submitted", label: "Submitted" },
    { value: "pending", label: "Under review" },
    { value: "confirmed", label: "Confirmed" },
    { value: "on_hold", label: "On hold" },
    { value: "rejected", label: "Rejected" },
    { value: "cancelled", label: "Cancelled" },
    { value: "completed", label: "Completed" },
  ],
  passengers: [
    { value: "draft", label: "Draft" },
    { value: "submitted", label: "Submitted" },
    { value: "under_review", label: "Under review" },
    { value: "processing", label: "Processing" },
    { value: "visa_approved", label: "Visa approved" },
    { value: "visa_issued", label: "Visa issued" },
    { value: "action_required", label: "Action required" },
    { value: "rejected", label: "Rejected" },
  ],
  payments: [
    { value: "pending_verification", label: "Pending verification" },
    { value: "received", label: "Received" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "refund_pending", label: "Refund pending" },
    { value: "refunded", label: "Refunded" },
  ],
  outstanding: [
    { value: "unpaid", label: "Unpaid" },
    { value: "pending", label: "Proof pending" },
    { value: "partially_paid", label: "Partially paid" },
  ],
  cancellations: [
    { value: "cancelled", label: "Cancelled" },
    { value: "rejected", label: "Rejected" },
    { value: "refund_pending", label: "Refund pending" },
    { value: "refunded", label: "Refunded" },
  ],
  documents: [
    { value: "draft", label: "Draft" },
    { value: "submitted", label: "Submitted" },
    { value: "under_review", label: "Under review" },
    { value: "action_required", label: "Action required" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ],
  visa: [
    { value: "draft", label: "Draft" },
    { value: "submitted", label: "Submitted" },
    { value: "under_review", label: "Under review" },
    { value: "processing", label: "Processing" },
    { value: "visa_approved", label: "Visa approved" },
    { value: "visa_issued", label: "Visa issued" },
    { value: "action_required", label: "Action required" },
    { value: "rejected", label: "Rejected" },
  ],
  travel: [
    { value: "not_assigned", label: "Not assigned" },
    { value: "partially_assigned", label: "Partially assigned" },
    { value: "confirmed", label: "Confirmed" },
    { value: "travel_ready", label: "Travel ready" },
    { value: "in_travel", label: "In travel" },
    { value: "completed", label: "Completed" },
  ],
  customers: [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ],
  notifications: [
    { value: "queued", label: "Queued" },
    { value: "sent", label: "Sent" },
    { value: "failed", label: "Failed" },
    { value: "read", label: "Read" },
  ],
};

const MONEY_KEYS = ["amount", "total_amount", "total_spent", "booking_total", "successful_payments", "pending_payments", "refunded_amount", "refund_pending", "outstanding"];
const STATUS_KEYS = ["booking_status", "payment_status", "travel_status", "readiness_status", "visa_status", "review_status", "status", "cust_status", "voucher_status", "internal_status"];
const PERSON_KEYS = ["customer_name", "passenger_name", "cust_name"];
const DATE_KEYS = ["date", "issued_at", "date_of_birth", "passport_expiry", "departure_date", "booking_date", "transaction_date", "created_date"];

function formatCurrency(value) {
  const amount = Number(value) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(url);
}

function exportExtension(format) {
  if (format === "excel") return "xlsx";
  return format;
}

function getColumns(reportType) {
  const columns = {
    bookings: ["booking_reference", "customer_name", "package_name", "booking_status", "payment_status", "travel_status", "departure_date", "total_amount"],
    passengers: ["booking_reference", "passenger_name", "passport_no", "passport_expiry", "passenger_type", "visa_status", "package_name", "booking_status"],
    payments: ["transaction_id", "booking_reference", "amount", "status", "transaction_kind", "transaction_date"],
    outstanding: ["booking_reference", "customer_name", "package_name", "booking_status", "booking_total", "successful_payments", "outstanding", "payment_status"],
    cancellations: ["booking_reference", "customer_name", "package_name", "booking_status", "total_amount", "refunded_amount", "refund_pending", "payment_status"],
    customers: ["registration_id", "cust_name", "cust_email", "cust_mobile", "cust_status", "total_bookings", "total_spent"],
    visa: ["booking_reference", "passenger_name", "visa_status", "internal_status", "visa_number", "issued_at"],
    documents: ["booking_reference", "passenger_name", "document_type", "review_status", "created_date"],
    travel: ["booking_reference", "travel_status", "readiness_status", "departure_date", "voucher_status"],
    notifications: ["recipient_type", "subject", "event_type", "status", "created_date"],
  };

  return columns[reportType] || columns.bookings;
}

function columnLabel(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function isDateKey(key) {
  return DATE_KEYS.some((part) => key === part || key.endsWith(`_${part}`) || key.includes("date"));
}

function renderValue(key, value, row) {
  if (value === null || value === undefined || value === "") return "-";
  if (MONEY_KEYS.includes(key)) return formatCurrency(value);
  if (isDateKey(key)) return formatDate(value);
  if (STATUS_KEYS.includes(key)) return <StatusBadge value={value} />;
  if (key === "booking_reference" && row?.booking_id) {
    return (
      <Link to={`/bookings/${row.booking_id}`} className="agent-table-link">
        {String(value)}
      </Link>
    );
  }
  if (PERSON_KEYS.includes(key)) {
    return <PersonCell name={value} />;
  }
  return String(value);
}

function MiniStat({ label, value }) {
  return (
    <div className="col-6 col-xl-3">
      <div className="card border-0 shadow-sm h-100 radius-8">
        <div className="card-body p-24">
          <div className="text-uppercase text-secondary small fw-semibold mb-2">{label}</div>
          <h4 className="mb-0">{value}</h4>
        </div>
      </div>
    </div>
  );
}

const emptyFilters = {
  dateFrom: "",
  dateTo: "",
  status: "",
  search: "",
  departureDate: "",
  page: 1,
  limit: 20,
};

export default function ReportsPage() {
  const [reportType, setReportType] = useState("bookings");
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState(emptyFilters);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState("");
  const [agencyRole, setAgencyRole] = useState("");

  useEffect(() => {
    let alive = true;
    fetchCurrentAgent()
      .then((profile) => {
        if (alive) setAgencyRole(roleFromProfile(profile));
      })
      .catch(() => {
        if (alive) setAgencyRole("");
      });
    return () => {
      alive = false;
    };
  }, []);

  const visibleReportOptions = useMemo(
    () => (canAccessFinance(agencyRole)
      ? REPORT_OPTIONS
      : REPORT_OPTIONS.filter((option) => option.value !== "payments" && option.value !== "outstanding")),
    [agencyRole]
  );

  const columns = useMemo(() => getColumns(reportType), [reportType]);
  const statusOptions = STATUS_OPTIONS[reportType] || [];
  const usesDeparture = ["bookings", "passengers", "outstanding", "cancellations", "travel", "visa"].includes(reportType);

  useEffect(() => {
    if (visibleReportOptions.some((option) => option.value === reportType)) {
      return;
    }
    setReportType(visibleReportOptions[0]?.value || "bookings");
  }, [reportType, visibleReportOptions]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilters((prev) => (prev.search === searchInput ? prev : { ...prev, page: 1, search: searchInput }));
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const queryParams = useMemo(() => ({
    dateFrom: filters.dateFrom || undefined,
    dateTo: filters.dateTo || undefined,
    status: filters.status || undefined,
    search: filters.search.trim() || undefined,
    departureDate: filters.departureDate || undefined,
    page: filters.page,
    limit: filters.limit,
  }), [filters]);

  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        setLoading(true);
        const response = await agentApi.get(`/travel-partner/reports/${reportType}`, {
          params: queryParams,
        });
        if (!alive) return;
        setReport(response?.data || response || {});
      } catch (err) {
        if (!alive) return;
        showApiError(err, "LOAD_REPORT");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();

    return () => {
      alive = false;
    };
  }, [reportType, queryParams]);

  async function handleExport(format) {
    try {
      setExporting(format);
      const blob = await agentApi.get(`/travel-partner/reports/${reportType}/export`, {
        params: {
          ...queryParams,
          page: 1,
          limit: 500,
          format,
        },
        responseType: "blob",
      });
      downloadBlob(blob, `${reportType}-report.${exportExtension(format)}`);
      showSuccessAction("EXPORT_REPORT");
    } catch (err) {
      showApiError(err, "EXPORT_REPORT");
    } finally {
      setExporting("");
    }
  }

  const rows = Array.isArray(report?.data) ? report.data : [];
  const total = Number(report?.total) || 0;
  const pageCount = Math.max(1, Math.ceil(total / Number(filters.limit || 20)));
  const outstandingTotal = rows.reduce((sum, row) => sum + Number(row.outstanding || 0), 0);
  const refundedTotal = rows.reduce((sum, row) => sum + Number(row.refunded_amount || 0), 0);

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="Reports"
          title="Agency reports"
          description="Your bookings, passengers, collections, and documents. Exports stay limited to this agency."
          items={[DASHBOARD_CRUMB, { label: "Reports" }]}
          actions={
            <div className="page-toolbar">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => handleExport("csv")} disabled={!!exporting}>
                {exporting === "csv" ? "Exporting..." : "CSV"}
              </button>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => handleExport("xlsx")} disabled={!!exporting}>
                {exporting === "xlsx" ? "Exporting..." : "Excel"}
              </button>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => handleExport("pdf")} disabled={!!exporting}>
                {exporting === "pdf" ? "Exporting..." : "PDF"}
              </button>
            </div>
          }
        />

        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-base border-bottom-0 pt-16 px-24 pb-0">
            <Tabs
              tabs={visibleReportOptions.map((option) => ({ id: option.value, label: option.label }))}
              activeId={reportType}
              onChange={(value) => {
                setSearchInput("");
                setFilters(emptyFilters);
                setReportType(value);
              }}
              className="mb-0"
              ariaLabel="Report type"
              idPrefix="report-tab"
            />
          </div>
          <div className="card-body p-24">
            <h5 className="mb-3">Filters</h5>
            <div className="row g-3 align-items-end">
              <div className="col-12 col-lg-4">
                <label className="form-label" htmlFor="report-search">Search</label>
                <input
                  id="report-search"
                  type="search"
                  className="form-control"
                  placeholder="Reference, customer, passenger, package"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                />
              </div>
              <div className="col-12 col-sm-6 col-lg-2">
                <label className="form-label" htmlFor="report-from">From</label>
                <input id="report-from" type="date" className="form-control" value={filters.dateFrom} onChange={(event) => setFilters((prev) => ({ ...prev, page: 1, dateFrom: event.target.value }))} />
              </div>
              <div className="col-12 col-sm-6 col-lg-2">
                <label className="form-label" htmlFor="report-to">To</label>
                <input id="report-to" type="date" className="form-control" value={filters.dateTo} onChange={(event) => setFilters((prev) => ({ ...prev, page: 1, dateTo: event.target.value }))} />
              </div>
              {statusOptions.length > 0 ? (
                <div className="col-12 col-sm-6 col-lg-2">
                  <label className="form-label" htmlFor="report-status">Status</label>
                  <AgentSelect
                    inputId="report-status"
                    options={statusOptions}
                    value={filters.status}
                    isClearable
                    placeholder="All statuses"
                    onChange={(value) => setFilters((prev) => ({ ...prev, page: 1, status: value || "" }))}
                  />
                </div>
              ) : null}
              {usesDeparture ? (
                <div className="col-12 col-sm-6 col-lg-2">
                  <label className="form-label" htmlFor="report-departure">Departure</label>
                  <input id="report-departure" type="date" className="form-control" value={filters.departureDate} onChange={(event) => setFilters((prev) => ({ ...prev, page: 1, departureDate: event.target.value }))} />
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {loading ? <PageLoader message="Loading report..." /> : null}

        {!loading ? (
          <>
            <div className="row g-4 mb-4">
              <MiniStat label="Total records" value={total} />
              <MiniStat label="Current page" value={`${report?.page || 1} / ${pageCount}`} />
              {reportType === "outstanding" ? (
                <MiniStat label="Outstanding on page" value={formatCurrency(outstandingTotal)} />
              ) : reportType === "cancellations" ? (
                <MiniStat label="Refunded on page" value={formatCurrency(refundedTotal)} />
              ) : (
                <MiniStat label="Rows per page" value={report?.limit || filters.limit} />
              )}
              <MiniStat label="Returned" value={rows.length} />
            </div>

            <div className="card border-0 shadow-sm">
              <div className="card-body p-24">
                <Table>
                  <thead>
                    <tr>
                      {columns.map((key) => (
                        <th key={key} className={MONEY_KEYS.includes(key) ? "text-end" : ""}>
                          {columnLabel(key)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={columns.length} className="text-center text-secondary py-4">No records found.</td>
                      </tr>
                    ) : (
                      rows.map((row, index) => (
                        <tr key={row.id || row.booking_id || row.passenger_id || row.transaction_id || row.customer_id || row.visa_id || row.document_id || row.notification_id || index}>
                          {columns.map((key) => (
                            <td key={key} className={MONEY_KEYS.includes(key) ? "text-end" : ""}>
                              {renderValue(key, row?.[key], row)}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>

                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-4">
                  <div className="small text-secondary">Showing {rows.length} of {total} records</div>
                  <div className="d-flex flex-wrap gap-2">
                    <button type="button" className="btn btn-outline-secondary btn-sm" disabled={(report?.page || 1) <= 1} onClick={() => setFilters((prev) => ({ ...prev, page: Math.max(1, (prev.page || 1) - 1) }))}>Prev</button>
                    <button type="button" className="btn btn-outline-secondary btn-sm" disabled={(report?.page || 1) >= pageCount} onClick={() => setFilters((prev) => ({ ...prev, page: Math.min(pageCount, (prev.page || 1) + 1) }))}>Next</button>
                    {canAccessFinance(agencyRole) ? (
                      <Link to="/wallet-credit" className="btn btn-outline-primary btn-sm">Outstanding</Link>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </AgentLayout>
  );
}
